import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { AuthContext } from './AuthContext';
import api from '../api';
import useListingsFreshness from '../hooks/useListingsFreshness';
import { isActiveListing } from '../utils/listingStatus';

const CompanyContext = createContext(null);


export const CompanyProvider = ({ children }) => {
    const { auth } = useContext(AuthContext);
    const [user, setUser] = useState(null);
    const [company, setCompany] = useState(null);
    const [notificationCount, setNotificationCount] = useState(0);
    const [activeListingsCount, setActiveListingsCount] = useState(0);
    // Whether activeListingsCount reflects a read that actually landed.
    //
    // The count seeds at 0 and both /listings reads below swallow their errors,
    // so without this "the read failed" and "this company has no listings"
    // arrive identically: 0, with loading already false. Callers route on that
    // difference (CompanyDashboardPage sends an empty company to Settings), and
    // a transient 500 must not relocate a company that has listings. Unknown is
    // a third state, not zero.
    const [activeListingsCountLoaded, setActiveListingsCountLoaded] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // Every authoritative local mutation advances this revision. Profile reads
    // capture the value they started under and may update React state only if
    // it is still current when they finish. Cache invalidation alone cannot
    // stop an older request's original caller from applying its stale result.
    const profileRevisionRef = useRef(0);

    // The only place the count is allowed to become known. A payload that is
    // not an array is a shape we cannot count, so it leaves the count unknown
    // rather than reporting a zero nobody can distinguish from a real one.
    const applyListingsCount = useCallback((payload) => {
        if (!Array.isArray(payload)) return;
        setActiveListingsCount(
            payload.filter(l => isActiveListing(l)).length
        );
        setActiveListingsCountLoaded(true);
    }, []);

    /*
     * A COUNT SOMEBODY ELSE ALREADY PAID FOR.
     *
     * The dashboard reads GET /listings on every mount and computes this exact
     * number with this exact filter (companyDashboardService's `activeListings`
     * uses the shared open, reviewed, undeleted predicate above). Before this,
     * the two answers lived side by side and
     * disagreed in public: the topbar said "12 Active Listings" over a dashboard
     * correctly reporting 0, because the badge was a mount-time snapshot and the
     * dashboard was current. Handing the answer over costs no request at all and
     * makes that particular disagreement impossible.
     *
     * VALIDATED, because a caller whose own read did not land has no number to
     * publish. Anything that is not a real count leaves the context untouched
     * rather than becoming a new way to invent a zero.
     */
    const reportListingsCount = useCallback((count) => {
        if (!Number.isInteger(count) || count < 0) return;
        setActiveListingsCount(count);
        setActiveListingsCountLoaded(true);
    }, []);

    // Fetch all shared data on mount
    useEffect(() => {
        if (!auth?.token) return;
        let cancelled = false;
        const requestRevision = profileRevisionRef.current;

        const fetchAll = async () => {
            try {
                setLoading(true);

                // Parallel fetch: profile + notifications.
                // Both endpoints are in api.js's DEDUPE_RULES, so going through
                // the shared axios instance lets these merge with the same reads
                // that AuthContext and the group contexts issue on the same page
                // load. Bare fetch() got none of that: a company page was making
                // 7 separate /auth/me requests.
                const [authRes, notifRes] = await Promise.allSettled([
                    api.get('/auth/me'),
                    api.get('/notifications/counts'),
                ]);

                if (cancelled) return;

                // Parse profile
                if (
                    authRes.status === 'fulfilled' &&
                    requestRevision === profileRevisionRef.current
                ) {
                    const data = authRes.value.data;
                    setUser({
                        _id: data._id,
                        firstName: data.firstName,
                        lastName: data.lastName,
                        email: data.email,
                        role: data.role,
                        groupId: data.groupId,
                    });
                    setCompany(data.group || null);

                    // Skip listings fetch for non-approved groups — the backend
                    // will 403 and the call is useless while under review.
                    const reviewStatus = data?.group?.reviewStatus;
                    const isGroup = data?.role === 'GROUP';
                    const isApproved = !isGroup || reviewStatus === 'approved';
                    if (isApproved) {
                        try {
                            const lr = await api.get('/listings');
                            if (!cancelled) applyListingsCount(lr.data);
                        } catch { /* non-critical */ }
                    }
                }

                // Parse notification count
                if (notifRes.status === 'fulfilled') {
                    const notifData = notifRes.value.data || {};
                    setNotificationCount(notifData.unreadCount || notifData.unread || 0);
                }
            } catch (err) {
                if (!cancelled) setError(err.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchAll();
        return () => { cancelled = true; };
    }, [auth?.token, applyListingsCount]);

    // Refresh functions — called by child screens after mutations.
    //
    // These are the post-mutation reads, so they MUST bust the cache first.
    // /auth/me carries a 60s TTL: a refresh that skipped the bust would be
    // served the pre-mutation snapshot and the caller would conclude the write
    // did not land. That is the accept-loop bug, and it is the reason these
    // used bare fetch() in the first place. Busting keeps the cache benefit on
    // the read path without the staleness on the write path.
    const refreshProfile = useCallback(async (updatedGroup = null) => {
        const requestRevision = ++profileRevisionRef.current;

        // A successful group PUT already returned the authoritative document.
        // Trust it and finish immediately. A second /auth/me read made the save
        // button wait on unrelated profile work and, while the server cache was
        // stale, replaced this value with the pre-save name.
        if (updatedGroup && typeof updatedGroup === 'object') {
            setCompany((current) => ({ ...(current || {}), ...updatedGroup }));
            api.invalidateCache('/auth/me');
            return;
        }
        if (!auth?.token) return;
        try {
            api.invalidateCache('/auth/me');
            const res = await api.get('/auth/me');
            if (requestRevision !== profileRevisionRef.current) return;
            const data = res.data;
            setUser({
                _id: data._id,
                firstName: data.firstName,
                lastName: data.lastName,
                email: data.email,
                role: data.role,
                groupId: data.groupId,
            });
            setCompany(data.group || null);
        } catch { /* non-critical */ }
    }, [auth?.token]);

    const refreshNotifications = useCallback(async () => {
        if (!auth?.token) return;
        try {
            api.invalidateCache('/notifications/counts');
            const res = await api.get('/notifications/counts');
            const data = res.data || {};
            setNotificationCount(data.unreadCount || data.unread || 0);
        } catch { /* non-critical */ }
    }, [auth?.token]);

    const refreshListingsCount = useCallback(async () => {
        if (!auth?.token) return;
        // Respect review-status gating: non-approved groups would just get a 403.
        try {
            const raw = localStorage.getItem('groupApprovalCache');
            if (raw) {
                const parsed = JSON.parse(raw);
                if (parsed.role === 'GROUP' && parsed.reviewStatus !== 'approved') return;
            }
        } catch { /* ignore */ }
        try {
            const res = await api.get('/listings');
            applyListingsCount(res.data);
        } catch { /* non-critical */ }
    }, [auth?.token, applyListingsCount]);

    /*
     * The badge is only as good as its last read, and CompanyProvider never
     * remounts inside /company/*, so without this the count is a mount-time
     * snapshot for the whole session. The rule lives in useListingsFreshness
     * because CompanyDashboardPage needs the SAME one: refreshing the badge on
     * focus while the numbers under it stayed on their own older read would
     * just move the disagreement rather than end it.
     *
     * Note this never clears the count first. `applyListingsCount` writes only
     * on a successful array, so a read in flight keeps showing the last known
     * number and a failed one changes nothing. That is not tidiness:
     * `activeListingsCountLoaded` is monotonic, so a count cleared to 0
     * mid-read would sail past CompanyDashboardPage's `!activeListingsCountLoaded`
     * guard and redirect a company that HAS listings to Settings on every
     * refresh.
     */
    useListingsFreshness(refreshListingsCount, !!auth?.token);

    return (
        <CompanyContext.Provider value={{
            company,
            user,
            notificationCount,
            activeListingsCount,
            activeListingsCountLoaded,
            loading,
            error,
            refreshProfile,
            refreshNotifications,
            refreshListingsCount,
            reportListingsCount,
        }}>
            {children}
        </CompanyContext.Provider>
    );
};

export const useCompany = () => {
    const ctx = useContext(CompanyContext);
    if (!ctx) {
        console.warn('[useCompany] Used outside CompanyProvider — returning defaults');
        return {
            company: null, user: null, notificationCount: 0, activeListingsCount: 0,
            activeListingsCountLoaded: false,
            loading: true, error: null,
            refreshProfile: () => { }, refreshNotifications: () => { }, refreshListingsCount: () => { },
            reportListingsCount: () => { },
        };
    }
    return ctx;
};

export default CompanyContext;
